import api from 'apis/api';
import filterApiErr from 'apis/filterApiError';
import { objToQueryParam } from 'apis/objToQueryParam';

import { PaginationInfo } from 'models/PaginationInfo';
import { Auction, AuctionPatch, AuctionState } from 'models/auction/Auction';
import { Image } from 'models/auction/Image';

import ERR_MSG from 'utils/errorMessage';

export const fetchAuctions = async (page?: number | null, marketplaceId?: string | null, per_page?: number) => {
  const queryObj = {
    page,
    per_page,
  };

  try {
    const resp = await api.get(
      `/api/v2/marketplaces/${marketplaceId ?? '_default'}/auctions${objToQueryParam(queryObj)}`
    );
    const data = resp.data as { auctions: Auction[]; pagination: PaginationInfo };
    return Promise.resolve(data);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
    }

    return Promise.reject(errMsg);
  }
};

export const getUserOwnAuctions = async (userId: string, page: number, per_page?: number) => {
  const queryObj = {
    page,
    per_page,
  };

  try {
    const resp = await api.get(`/api/v1/users/${userId}/profile/auctions${objToQueryParam(queryObj)}`);
    const data = resp.data as { auctions: Auction[]; pagination: PaginationInfo };
    return Promise.resolve(data);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      const errMsgFromServer = err.response.data.message;
      if (errMsgFromServer) errMsg = errMsgFromServer;
      else errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
    }

    return Promise.reject(errMsg);
  }
};

export const fetchMyAuctions = async (page?: number | null, state?: AuctionState, per_page?: number) => {
  const queryObj = {
    page,
    per_page,
    state,
  };

  try {
    const resp = await api.get(`/api/v2/auctions${objToQueryParam(queryObj)}`);
    const data = resp.data as { auctions: Auction[]; pagination: PaginationInfo };
    return Promise.resolve(data);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      const status = err.response.status;
      if (status === 401) {
        errMsg = '로그인이 필요합니다.';
      } else if (status === 403) {
        errMsg = '판매자 권한이 없습니다.';
      } else {
        const errMsgFromServer = err.response.data.message;
        if (errMsgFromServer) errMsg = errMsgFromServer;
        else errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
      }
    }

    return Promise.reject(errMsg);
  }
};

export const fetchAuction = async (auctionId?: string | null) => {
  if (!auctionId) return Promise.reject(`${ERR_MSG.NOT_FOUND} ${ERR_MSG.BAD_INPUT}`);

  try {
    const resp = await api.get(`/api/v1/auctions/${auctionId}`);
    const data = resp.data as Auction;
    return Promise.resolve(data);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
    }

    return Promise.reject(errMsg);
  }
};

export const startCreateAuction = async () => {
  try {
    const resp = await api.post('/api/v1/auctions');
    const data = resp.data as Auction;
    return Promise.resolve(data);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      const status = err.response.status;
      if (status === 401) {
        errMsg = '경매 생성 권한이 없습니다.';
      } else {
        const errMsgFromServer = err.response.data.message;
        if (errMsgFromServer) errMsg = errMsgFromServer;
        else errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
      }
    }

    return Promise.reject(errMsg);
  }
};

export const finalizeCreateAuction = async (auctionId: string) => {
  try {
    const resp = await api.post(`/api/v1/auctions/${auctionId}/finalize`);
    const data = resp.data as Auction;
    return Promise.resolve(data);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
    }

    return Promise.reject(errMsg);
  }
};

export const patchAuction = async (auctionId: string, auctionPatch: AuctionPatch) => {
  try {
    const resp = await api.patch(`/api/v1/auctions/${auctionId}`, auctionPatch);
    const data = resp.data as Auction;
    return Promise.resolve(data);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
    }

    return Promise.reject(errMsg);
  }
};

export const cancelAuction = async (auctionId: string) => {
  try {
    await api.post(`/api/v1/auctions/${auctionId}/cancel`);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      const status = err.response.status;
      if (status === 404) {
        errMsg = '존재하지 않는 경매입니다.';
      } else {
        const errMsgFromServer = err.response.data.message;
        if (errMsgFromServer) errMsg = errMsgFromServer;
        else errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
      }
    }

    return Promise.reject(errMsg);
  }
};

export const deleteAuction = async (auctionId: string) => {
  try {
    await api.delete(`/api/v1/auctions/${auctionId}`);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      const status = err.response.status;
      if (status === 400) {
        errMsg = '올바르지 않은 요청입니다.';
      } else if (status === 401) {
        errMsg = '인증되지 않은 요청입니다.';
      } else if (status === 403) {
        errMsg = '삭제 권한이 없습니다.';
      } else if (status === 404) {
        errMsg = '존재하지 않는 경매입니다.';
      } else {
        const errMsgFromServer = err.response.data.message;
        if (errMsgFromServer) errMsg = errMsgFromServer;
        else errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
      }
    }

    return Promise.reject(errMsg);
  }
};

export const uploadImage = async (auctionId: string, image: File) => {
  try {
    const resp = await api.postForm(`/api/v1/auctions/${auctionId}/images`, { image_blobs: image }, { timeout: 15000 });
    const data = resp.data.images as Image[];
    const uploadedImage = data[0];

    return Promise.resolve(uploadedImage);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      const errMsgFromServer = err.response.data.message;
      if (errMsgFromServer) {
        if (errMsgFromServer === 'Image is too small to make a thumbnail')
          errMsg = '256 x 256 이상의 이미지만 등록할 수 있어 해당 이미지는 삭제되었습니다.';
        else errMsg = errMsgFromServer;
      } else errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
    }

    return Promise.reject(errMsg);
  }
};

export const deleteImage = async (auctionId: string, imageId: string) => {
  try {
    await api.delete(`/api/v1/auctions/${auctionId}/images/${imageId}`);
  } catch (_err: any) {
    let errMsg = '';

    const err = filterApiErr(_err);
    if (err) {
      errMsg = `${ERR_MSG.UNKNOWN} ${ERR_MSG.REPORT}`;
    }

    return Promise.reject(errMsg);
  }
};
